import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatMoney } from '@/lib/money';

import { ModuleSelectionField } from './ModuleSelectionField';
import { getTenantSubscription, tenantSubscriptionQueryKey } from '../api/subscription.graphql';
import {
  MODULE_REASON_LIMITS,
  setTenantModules,
  type TenantModule,
} from '../api/tenant-modules.graphql';
import {
  selectionCheckState,
  useModulePresets,
  useModuleSelectionCheck,
} from '../hooks/useModuleCatalog';
import { moduleNames } from '../module-labels';

interface Props {
  tenantId: string;
  modules: TenantModule[];
  onDone: (modules: TenantModule[]) => void;
  onRejected: () => void;
  onCancel: () => void;
  /** OS-1723 — `platform.billing.read`: mostrar qué líneas de suscripción se cierran. */
  canReadBilling?: boolean;
}

/**
 * OS-1716 — cambiar varios módulos a la vez, con presets que pre-marcan la selección. El API
 * valida la selección (dependencias) mientras se edita y otra vez al guardar, con el tenant
 * bloqueado; cada módulo que cambia queda auditado con el motivo.
 */
export function ModuleBulkDialog({
  tenantId,
  modules,
  onDone,
  onRejected,
  onCancel,
  canReadBilling = false,
}: Props): JSX.Element {
  const current = modules.filter((m) => m.enabled).map((m) => m.key);
  const [selection, setSelection] = useState<string[]>(current);
  const [preset, setPreset] = useState<string | null>(null);
  const presets = useModulePresets();
  const check = selectionCheckState(useModuleSelectionCheck(selection));
  const names = new Map(modules.map((m) => [m.key, m.name]));

  const changeable = modules.filter((m) => !m.isCore);
  const toEnable = changeable.filter((m) => !m.enabled && selection.includes(m.key)).map((m) => m.key);
  const toDisable = changeable.filter((m) => m.enabled && !selection.includes(m.key)).map((m) => m.key);
  const hasChanges = toEnable.length + toDisable.length > 0;
  // OS-1723 — deshabilitar un módulo que se cobra cierra su línea: se muestra antes el impacto.
  const subscription = useQuery({
    queryKey: tenantSubscriptionQueryKey(tenantId),
    queryFn: () => getTenantSubscription(tenantId),
    enabled: canReadBilling,
    staleTime: 0,
  });
  const closing = (subscription.data?.items ?? []).filter((item) =>
    toDisable.includes(item.moduleKey),
  );
  const mrrDrop = closing.reduce((sum, item) => sum + item.monthlyAmount, 0);

  const mutation = useMutation({
    mutationFn: (reason: string) =>
      setTenantModules({ tenantId, modules: selection, preset, reason }),
    onSuccess: onDone,
    onError: onRejected,
  });

  return (
    <ConfirmDialog
      title="Cambiar módulos del tenant"
      confirmLabel="Guardar módulos"
      cancelLabel="Volver"
      danger={toDisable.length > 0}
      pending={mutation.isPending}
      confirmDisabled={!hasChanges || check.state !== 'valid'}
      error={mutation.error?.message ?? null}
      reason={{
        label: 'Motivo (queda en la auditoría de cada módulo)',
        placeholder: 'Ej.: el cliente cambió al paquete de operaciones',
        ...MODULE_REASON_LIMITS,
      }}
      onConfirm={(reason) => mutation.mutate(reason)}
      onCancel={onCancel}
    >
      {presets.isError ? (
        <p role="alert">No se pudieron cargar los presets; puedes elegir los módulos uno a uno.</p>
      ) : null}
      <ModuleSelectionField
        catalog={modules}
        presets={presets.data ?? []}
        value={selection}
        current={current}
        check={check}
        onChange={(next, presetKey) => {
          setSelection(next);
          setPreset(presetKey);
        }}
      />
      <p aria-live="polite">
        {hasChanges
          ? [
              toEnable.length > 0 ? `Se habilitarán: ${moduleNames(toEnable, names)}.` : '',
              toDisable.length > 0 ? `Se deshabilitarán: ${moduleNames(toDisable, names)}.` : '',
            ]
              .filter(Boolean)
              .join(' ')
          : 'Sin cambios respecto a los módulos actuales.'}
      </p>
      {closing.length > 0 ? (
        <p aria-live="polite">
          Se cerrarán sus líneas de suscripción ({closing.map((item) => item.moduleName).join(', ')}
          ): el MRR baja {formatMoney(Math.round(mrrDrop * 100) / 100)} al mes. Los módulos que se
          habiliten no se cobran hasta agregarlos en Facturación.
        </p>
      ) : null}
    </ConfirmDialog>
  );
}
